import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Users, Search, UserPlus } from "lucide-react";

const EMPTY_ATTENDANT = { name: "", email: "", password: "", phone: "" };

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAttendant, setShowAttendant] = useState(false);
  const [attendantForm, setAttendantForm] = useState(EMPTY_ATTENDANT);
  const [savingAttendant, setSavingAttendant] = useState(false);
  const [stats, setStats] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const navigate = useNavigate();

  const toggleUserActive = async (user) => {
    const deactivating = user.active !== false;
    if (deactivating && !window.confirm(`Deactivate ${user.name}? They will be logged out of all devices and unable to log in.`)) {
      return;
    }
    setTogglingId(user._id);
    try {
      await axios.put(`${API}/admin/users/${user._id}/active`, { active: !deactivating });
      toast.success(deactivating ? "User deactivated" : "User reactivated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createAttendant = async (e) => {
    e.preventDefault();
    if (!attendantForm.name || !attendantForm.email || !attendantForm.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSavingAttendant(true);
    try {
      await axios.post(`${API}/admin/attendants`, attendantForm);
      toast.success("Attendant account created");
      setShowAttendant(false);
      setAttendantForm(EMPTY_ATTENDANT);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create attendant");
    } finally {
      setSavingAttendant(false);
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, vehiclesRes, bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/vehicles`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/stats?days=14`),
      ]);
      setUsers(usersRes.data);
      setVehicles(vehiclesRes.data);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-users-page">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-admin"
          onClick={() => navigate("/admin")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Admin
        </Button>

        <div className="fade-in">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">User Management</h1>
              <p className="text-xl text-gray-600">Monitor all registered users and their activities.</p>
            </div>
            <Dialog open={showAttendant} onOpenChange={setShowAttendant}>
              <DialogTrigger asChild>
                <Button className="bg-[#1A3C34] hover:bg-[#15302a] text-white rounded-full">
                  <UserPlus className="mr-2" size={18} />
                  Add Attendant
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Parking Attendant</DialogTitle>
                </DialogHeader>
                <form onSubmit={createAttendant} className="space-y-4">
                  <Input
                    placeholder="Full name"
                    value={attendantForm.name}
                    onChange={(e) => setAttendantForm({ ...attendantForm, name: e.target.value })}
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={attendantForm.email}
                    onChange={(e) => setAttendantForm({ ...attendantForm, email: e.target.value })}
                  />
                  <Input
                    type="password"
                    placeholder="Temporary password"
                    value={attendantForm.password}
                    onChange={(e) => setAttendantForm({ ...attendantForm, password: e.target.value })}
                  />
                  <Input
                    placeholder="Phone (optional)"
                    value={attendantForm.phone}
                    onChange={(e) => setAttendantForm({ ...attendantForm, phone: e.target.value })}
                  />
                  <Button type="submit" disabled={savingAttendant} className="w-full bg-[#1A3C34] text-white">
                    {savingAttendant ? "Creating…" : "Create Attendant"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Search Bar */}
          <div className="mb-6 relative">
            <Search
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <Input
              data-testid="search-users"
              placeholder="Search by name, email, or organization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
            />
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 bg-[#1A3C34] text-white border-none">
              <p className="text-sm opacity-90 mb-2">Total Users</p>
              <p className="text-3xl font-bold">{users.length}</p>
            </Card>
            <Card className="p-6 bg-[#F5F2EB] border-none">
              <p className="text-sm text-gray-600 mb-2">Total Vehicles</p>
              <p className="text-3xl font-bold text-[#1A3C34]">{vehicles.length}</p>
            </Card>
            <Card className="p-6 bg-white border border-gray-100">
              <p className="text-sm text-gray-600 mb-2">Active Bookings</p>
              <p className="text-3xl font-bold text-[#1A3C34]">
                {bookings.filter((b) => b.status === "active").length}
              </p>
            </Card>
            <Card className="p-6 bg-[#FF6B35] text-white border-none">
              <p className="text-sm opacity-90 mb-2">Organizations</p>
              <p className="text-3xl font-bold">
                {new Set(users.map((u) => u.organization)).size}
              </p>
            </Card>
          </div>

          {/* Analytics */}
          {stats && (
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {/* Per-day booking trend */}
              <Card className="p-6 border border-gray-100 lg:col-span-1">
                <h3 className="text-lg font-bold text-[#1A3C34] mb-1">Bookings / day</h3>
                <p className="text-sm text-gray-500 mb-4">Last {stats.per_day_bookings.length} days</p>
                {(() => {
                  const max = Math.max(1, ...stats.per_day_bookings.map((d) => d.count));
                  return (
                    <div className="flex items-end gap-1 h-32">
                      {stats.per_day_bookings.map((d) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                          <div
                            className="w-full bg-[#1A3C34] rounded-t"
                            style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? 4 : 0 }}
                          />
                          <span className="text-[9px] text-gray-400 mt-1">{d.date.slice(8)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </Card>

              {/* Top bookers */}
              <Card className="p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-[#1A3C34] mb-4">Top bookers</h3>
                {stats.top_bookers.length === 0 ? (
                  <p className="text-sm text-gray-500">No bookings yet.</p>
                ) : (
                  <div className="space-y-3">
                    {stats.top_bookers.map((u, i) => (
                      <div key={u.user_id} className="flex items-center justify-between">
                        <span className="text-gray-800">
                          <span className="text-gray-400 mr-2">#{i + 1}</span>
                          {u.name}
                        </span>
                        <span className="px-3 py-1 bg-[#F5F2EB] text-[#1A3C34] rounded-full text-sm font-semibold">
                          {u.bookings} bookings
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Flagged / bad users */}
              <Card className="p-6 border border-red-100 bg-red-50/40">
                <h3 className="text-lg font-bold text-red-800 mb-1">Flagged users</h3>
                <p className="text-sm text-gray-500 mb-4">{stats.total_flags} total flags raised</p>
                {stats.flagged_users.length === 0 ? (
                  <p className="text-sm text-gray-500">No flags raised. 🎉</p>
                ) : (
                  <div className="space-y-3">
                    {stats.flagged_users.map((u) => (
                      <div key={u.owner_id || u.plates.join(",")} className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-gray-800 font-medium">{u.name}</p>
                          {u.plates.length > 0 && (
                            <p className="text-xs text-gray-500 font-mono">{u.plates.join(", ")}</p>
                          )}
                        </div>
                        <span className="px-3 py-1 bg-red-200 text-red-900 rounded-full text-sm font-semibold whitespace-nowrap">
                          {u.flag_count} flag{u.flag_count === 1 ? "" : "s"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Users Table */}
          <Card className="p-6 border border-gray-100">
            <h2 className="text-2xl font-bold text-[#1A3C34] mb-6">All Users</h2>
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-600">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">Name</th>
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">Email</th>
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">Organization</th>
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">City</th>
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">Phone</th>
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">Vehicles</th>
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">Status</th>
                      <th className="text-left py-3 px-4 text-[#1A3C34] font-bold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => {
                      const userVehicles = vehicles.filter((v) => v.user_id === user._id);
                      return (
                        <tr
                          key={user._id}
                          data-testid={`user-row-${user._id}`}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-4 px-4 font-medium text-gray-900">{user.name}</td>
                          <td className="py-4 px-4 text-gray-600">{user.email}</td>
                          <td className="py-4 px-4 text-gray-600">{user.organization}</td>
                          <td className="py-4 px-4 text-gray-600">{user.city}</td>
                          <td className="py-4 px-4 text-gray-600">{user.phone}</td>
                          <td className="py-4 px-4">
                            <span className="px-3 py-1 bg-[#1A3C34] text-white rounded-full text-sm font-medium">
                              {userVehicles.length}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {user.active === false ? (
                              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                                Deactivated
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <Button
                              size="sm"
                              variant={user.active === false ? "outline" : "destructive"}
                              disabled={togglingId === user._id}
                              onClick={() => toggleUserActive(user)}
                            >
                              {user.active === false ? "Reactivate" : "Deactivate"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;